/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Row,
  Table,
  Col,
  Typography,
  Modal,
  Form,
  notification,
  Select,
  Tooltip,
  InputNumber,
  Button,
  Checkbox,
  Skeleton,
  Input,
  Radio,
  Upload,
} from 'antd';
import { connect } from 'react-redux';
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileDoneOutlined,
  DownloadOutlined,
  CloudUploadOutlined,
} from '@ant-design/icons';
import { get } from 'lodash';
import AddNewShipments from './AddNewShipments';
import ShipmentLogs from './ShipmentLogs';
import { fetchShipmentTypeDetails } from '../../redux/ShipmentDetails/actions';
import { fetchShipmentDashBoardDetails } from '../../redux/ShipmentDashboardDetails/actions';
import { OnDeleteShipments } from '../../redux/ShipmentDashboardDetails/saga';
import axiosConfig from '../../redux/ShipmentDashboardDetails/axiosConfig';
import axios from 'axios';
import { AWS_IMAGE_URL } from '../../config';
import { packagingApiToFormValue } from './shipmentFieldOptions';

const { Title, Text } = Typography;

const priorityColText = (v) => {
  const key = String(v || '').toLowerCase();
  return ({ high: 'High', medium: 'Medium', low: 'Low' }[key] || v || '—');
};
const categoryColText = (v) => {
  const key = String(v || '')
    .toLowerCase()
    .replace(/-/g, '_');
  return (
    { project: 'Project', one_time_sales: 'One time sales' }[key] || v || '—'
  );
};
const packagingColText = (v) => {
  const form = packagingApiToFormValue(v);
  if (form) {
    const m = { PALLET: 'Pallet', CRATE_BOX: 'Crate box', UNPACKED: 'Unpacked' };
    return m[form];
  }
  return v || '—';
};

const { Option } = Select;

const ShipmentDashBoardPage = (props) => {
  const {
    selectedProject,
    onGetShipmentTypes,
    shipmentsTypes,
    shipmentTypesLoading,
    shipmentTypesCount,
    shipmentData,
    shipmentDataLoading,
    shipmentDataCount,
    OnGetShipmentsData,
    userAccessList,
    projectTeamMembersAccessList,
  } = props;

  const [projectIdValue, setProjectIdValue] = useState(false);
  const [equipmentFormData] = Form.useForm();

  const [showAddModel, setShowAddModel] = useState(false);
  const [isTableModelVisible, setIsTableModelVisible] = useState(false);
  const [editRecord, setEditRecord] = useState('');
  const [isEdit, setIsEdit] = useState('');
  const [adminShipmentTypesList, setAdminShipmentTypesList] = useState([]);

  const [currentPagesize, setCurrentPagesize] = useState(10);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isFileModalVisible, setIsFileModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [bulkFileList, setBulkFileList] = useState([]);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedData, setSelectedData] = useState('');
  const [searchKeyExist, setSearchKeyExist] = useState('');

  const [showFormModel, setShowFormModel] = useState(false);

  const [current, setCurrent] = useState(1);

  useEffect(() => {
    if (adminShipmentTypesList && adminShipmentTypesList.length == 0) onGetShipmentTypes();
  }, []);

  useEffect(() => {
    // console.log('ShipmentTypes:', shipmentsTypes.length);

    if (shipmentsTypes && shipmentsTypes.length > 0) setAdminShipmentTypesList(shipmentsTypes);
  }, [shipmentsTypes]);

  const isWriteAccess = useCallback(() => {
    if (userAccessList?.includes('admin') || userAccessList?.includes('super-admin')) return false;
    else if (projectTeamMembersAccessList?.[0]?.isGuestUser) {
      return true;
    } else if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !projectTeamMembersAccessList?.[0]?.tabPermissions?.Shipment?.write
    ) {
      return true;
    } else {
      return false;
    }
  }, [userAccessList, projectTeamMembersAccessList]);

  const renderStatusDot = (record, formType) => {
    const isActive =
      record.submissions && record.submissions.some((item) => item.formType === formType);
    const color = isActive ? 'limegreen' : 'orange';

    return (
      <Tooltip title={isActive ? 'Form submitted' : 'Form not submitted'}>
        <span className="icon-18 mr-2 cursor-pointer edit-hover">
          <span
            className="blinkingDot"
            style={{ background: color }}
          >
            <span
              className="blinking"
              style={{ background: color }}
            ></span>
          </span>
        </span>
      </Tooltip>
    );
  };

  /** Setting coloumn (headers) for Equipment table */
  const columns = useMemo(
    () => [
      {
        // title: 'PIH Equipment',
        dataIndex: 'sno',
        key: 'sno',
        align: 'center',
        width: '55px',
        render: (text, record, index) => {
          return (current - 1) * currentPagesize + (index + 1);
        },
      },
      {
        title: 'Shipment Type',
        dataIndex: 'shipmentTypeName',
        key: 'shipmentTypeName',
        align: 'left',
        ellipsis: {
          showTitle: false,
        },
      },

      {
        title: 'Shipment Number',
        dataIndex: 'shipmentNumber',
        key: 'shipmentNumber',
        align: 'center',
      },
      {
        title: 'Priority',
        dataIndex: 'priority',
        key: 'priority',
        align: 'center',
        width: 100,
        render: (v) => priorityColText(v),
      },
      {
        title: 'Category',
        dataIndex: 'shipmentCategory',
        key: 'shipmentCategory',
        align: 'left',
        ellipsis: true,
        render: (v) => categoryColText(v),
      },
      {
        title: 'Subcategory',
        key: 'packagingType',
        align: 'left',
        ellipsis: true,
        render: (_, record) =>
          packagingColText(record.packagingType ?? record.packagingSubcategory),
      },
      {
        title: 'PSI',
        key: 'psi',
        dataIndex: 'psi',
        width: '100px',
        render: (_, record) => renderStatusDot(record, 'Pre-Shipment Inspection'),
        align: 'center',
      },
      {
        title: 'SGR',
        key: 'sgr',
        dataIndex: 'sgr',
        width: '100px',
        render: (_, record) => renderStatusDot(record, 'Site Goods Received'),
        align: 'center',
      },
      {
        title: 'Actions',
        key: 'action',
        dataIndex: 'action',
        width: '250px',
        render: (text, record) => {
          const hasWriteAccess = isWriteAccess();
          return (
            <div>
              <Tooltip title="View Shipments">
                <span
                  className="icon-18 mr-2 cursor-pointer edit-hover"
                  style={{ marginRight: '20px' }}
                >
                  <EyeOutlined
                    style={{ fontSize: '18px' }}
                    onClick={() => {
                      handleEyeClick(record);
                    }}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Update">
                <span className="icon-18 mr-2 cursor-pointer edit-hover">
                  <EditOutlined
                    style={{
                      fontSize: '18px',
                      color: hasWriteAccess ? 'gray' : '',
                      opacity: hasWriteAccess ? '0.7' : '',
                    }}
                    onClick={() => {
                      !hasWriteAccess ? onShowEditModel(record) : undefined;
                    }}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <span
                  className="icon-18 mr-2 cursor-pointer edit-hover"
                  style={{ marginLeft: '10px' }}
                >
                  <DeleteOutlined
                    style={{
                      fontSize: '18px',
                      color: hasWriteAccess ? 'gray' : '',
                      opacity: hasWriteAccess ? '0.7' : '',
                    }}
                    onClick={() => {
                      !hasWriteAccess ? showDeleteModal(record) : undefined;
                    }}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Files">
                <span
                  className="icon-18 mr-2 cursor-pointer edit-hover"
                  style={{ marginLeft: '10px' }}
                >
                  <FileDoneOutlined
                    style={{ fontSize: '18px' }}
                    onClick={() => onUpdateFileClick(record)}
                  />
                </span>
              </Tooltip>
            </div>
          );
        },
        align: 'center',
      },
    ],
    [isWriteAccess, current, currentPagesize]
  );

  const columnsFile = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      align: 'left',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'left', height: '100%' }}>{text}</div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'edit_delete',
      key: 'edit_delete',
      align: 'center',
      render: (text, record) => {
        return (
          <div>
            <Tooltip title="Download">
              <Button
                href={`${AWS_IMAGE_URL}/${record.fileKey}`}
                className="p-0 border-0"
                size="small"
                target="_blank"
                style={{ marginLeft: '10px' }}
              >
                <DownloadOutlined className="pdfIconRed" />
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    if (rowData && rowData.length == 0) OnfetchData();
  }, []);

  useEffect(() => {
    if (shipmentData && shipmentData.length > 0) setRowData(shipmentData);
  }, [shipmentData]);

  const OnfetchData = async (page = 1, size = currentPagesize) => {
    setCurrent(page);
    setProjectIdValue(selectedProject.id);
    const obj = {
      pageSize: size,
      pageNumber: page,
      projectId: selectedProject.id,
    };
    await OnGetShipmentsData(obj);
  };

  const onShowEditModel = (record) => {
    setEditRecord(record);
    setIsEdit(true);
    setShowAddModel(true);
  };

  const onUpdateFileClick = (data) => {
    if (data) {
      setSelectedData(data);
      setIsFileModalVisible(true);
    }
  };

  const showDeleteModal = (record) => {
    setSelectedData(record);
    setIsDeleteModalVisible(true);
  };

  const handleOk = async () => {
    setDeleteLoading(true);
    await OnDeleteShipments(selectedData);
    await OnfetchData();
    setDeleteLoading(false);
    setIsDeleteModalVisible(false);
  };

  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedData('');
  };

  const handleBulkUploadCancel = () => {
    setBulkUploadModalVisible(false);
    setBulkFileList([]);
  };

  /** Close Modal on close of table details */
  const handleCancelTable = () => {
    setIsTableModelVisible(false);
    setShowFormModel(false);
  };
  /**Open checks table / Modal on view of checks (double click on row disabled)  */
  const handleEyeClick = async (record) => {
    console.log('Eye icon clicked for record:', record);
    setSelectedData(record);
    setIsTableModelVisible(true);
    setShowFormModel(true);
  };

  const onSearchEquip = async (event) => {
    setCurrent(1);
    // console.log(event.target.value)
    if (event.target.value) {
      console.log('here');
      console.log('event.target.value', event.target.value);
      setSearchKeyExist(event.target.value);
      await OnGetShipmentsData({
        projectId: selectedProject.id,
        searchKey: event.target.value,
      });
    } else {
      setSearchKeyExist('');
      var obj = {
        projectId: selectedProject.id,
        pageSize: 10,
        pageNumber: 1,
      };
      await OnGetShipmentsData(obj);
    }
  };

  // File upload functions
  const fileProps = {
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      setFileList((prev) => [...prev, file]);
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    accept: '.xlsx,.xls,.pdf',
    maxCount: 100,
  };

  // Bulk upload file props
  const bulkFileProps = {
    multiple: true,
    fileList: bulkFileList,
    beforeUpload: (file) => {
      setBulkFileList((prev) => [...prev, file]);
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      setBulkFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    accept: '.xlsx,.xls,.pdf',
    maxCount: 100,
  };

  const uploadFilesToS3 = async () => {
    try {
      const uploadedFiles = [];

      if (fileList && fileList.length > 0) {
        const fileObjs = fileList.map((file) => ({
          fileName: file.name.replace(/\(\d+\)/g, '').trim(),
          contentType: file.type,
        }));

        // Get signed URLs for multiple files using the shipment-specific endpoint
        const signedUrlRequests = fileObjs.map((fileObj) =>
          axiosConfig.get(
            `/get-shipment-pre-signed-url?fileName=${encodeURIComponent(
              fileObj.fileName
            )}&contentType=${encodeURIComponent(fileObj.contentType)}`
          )
        );

        const signedUrlResponses = await Promise.all(signedUrlRequests);

        if (signedUrlResponses.every((res) => res.status === 200)) {
          for (let i = 0; i < signedUrlResponses.length; i++) {
            const data = signedUrlResponses[i].data.data;
            const file = fileList[i];

            if (file && data) {
              try {
                const uploadResponse = await axios.put(data.presignedUrl, file, {
                  headers: {
                    'Content-Disposition': 'attachment',
                    'Content-Type': file.type || 'application/octet-stream',
                  },
                });

                if (uploadResponse.status === 200) {
                  uploadedFiles.push({
                    fileKey: data.fileKey,
                    fileName: data.fileName,
                  });
                } else {
                  console.error(`Error uploading file: ${file.name}`, uploadResponse);
                }
              } catch (error) {
                console.error(`Error processing file: ${file.name}`, error);
              }
            }
          }
        }

        setFileList([]);
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading files:', error);
      return []; // Return empty array on error to prevent "not iterable" error
    }
  };

  const handleUploadFiles = async () => {
    if (fileList.length === 0) {
      notification.warning({
        message: 'Please select files to upload.',
      });
      return;
    }

    setUploadLoading(true);
    try {
      const uploadedFiles = await uploadFilesToS3();

      if (uploadedFiles.length > 0) {
        // No transformation needed, uploadedFiles already has fileKey and fileName
        let existingDocuments = selectedData.document || [];
        const newDocuments = [...existingDocuments, ...uploadedFiles];

        const updateData = {
          id: selectedData.pihShipmentId,
          data: {
            document: newDocuments,
            // Only pass document field, keep other fields unchanged
          },
        };

        // Import the update function
        const { OnAddorUpdateShipmentsDashboard } = await import(
          '../../redux/ShipmentDashboardDetails/saga'
        );
        const res = await OnAddorUpdateShipmentsDashboard(updateData, true);

        if (res) {
          notification.success({
            message: 'Files uploaded successfully.',
          });
          await OnfetchData();
          setIsFileModalVisible(false);
        } else {
          notification.error({
            message: 'Failed to update files.',
          });
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error uploading files.',
        description: error.message,
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // Bulk upload function for shipment documents
  const uploadBulkFilesToS3 = async () => {
    try {
      const uploadedFiles = [];

      if (bulkFileList && bulkFileList.length > 0) {
        const fileObjs = bulkFileList.map((file) => ({
          fileName: file.name.replace(/\(\d+\)/g, '').trim(),
          contentType: file.type,
        }));

        // Get signed URLs for multiple files using the shipment-specific endpoint
        const signedUrlRequests = fileObjs.map((fileObj) =>
          axiosConfig.get(
            `/get-shipment-pre-signed-url?fileName=${encodeURIComponent(
              fileObj.fileName
            )}&contentType=${encodeURIComponent(fileObj.contentType)}`
          )
        );

        const signedUrlResponses = await Promise.all(signedUrlRequests);

        if (signedUrlResponses.every((res) => res.status === 200)) {
          for (let i = 0; i < signedUrlResponses.length; i++) {
            const data = signedUrlResponses[i].data.data;
            const file = bulkFileList[i];

            if (file && data) {
              try {
                const uploadResponse = await axios.put(data.presignedUrl, file, {
                  headers: {
                    'Content-Disposition': 'attachment',
                    'Content-Type': file.type || 'application/octet-stream',
                  },
                });

                if (uploadResponse.status === 200) {
                  uploadedFiles.push({
                    fileKey: data.fileKey,
                    fileName: data.fileName,
                  });
                } else {
                  console.error(`Error uploading file: ${file.name}`, uploadResponse);
                }
              } catch (error) {
                console.error(`Error processing file: ${file.name}`, error);
              }
            }
          }
        }

        setBulkFileList([]);
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading bulk files:', error);
      return []; // Return empty array on error to prevent "not iterable" error
    }
  };

  const handleBulkUploadFiles = async () => {
    if (bulkFileList.length === 0) {
      notification.warning({
        message: 'Please select files to upload.',
      });
      return;
    }

    setBulkUploadLoading(true);
    try {
      const uploadedFiles = await uploadBulkFilesToS3();

      if (uploadedFiles.length > 0) {
        // No transformation needed, uploadedFiles already has fileKey and fileName
        // For bulk upload, you might want to show a modal to select which shipment each file belongs to
        // For now, we'll just show success message
        notification.success({
          message: `${uploadedFiles.length} files uploaded successfully.`,
        });
        // TODO: Add logic to associate files with specific shipments
        setBulkUploadModalVisible(false);
        await OnfetchData(); // Refresh the data
      }
    } catch (error) {
      notification.error({
        message: 'Error uploading files.',
        description: error.message,
      });
    } finally {
      setBulkUploadLoading(false);
    }
  };

  return (
    <div className="h-100 overflow-y-hidden overflow-x-hidden equipmentsPage">
      <div className="flex-auto">
        <div className="d-flex mb-3 align-items-center tab-content-title">
          <Title
            className="mb-0"
            level={4}
          >
            List of Shipments
          </Title>
          <div className="searchEquip">
            <Input
              placeholder="Search Shipments"
              onChange={onSearchEquip}
              value={searchKeyExist}
              allowClear
            />
          </div>

          <div className="inspection-logs">
            <div className="new-project-team">
              <Text
                onClick={() => {
                  if (!isWriteAccess()) {
                    setEditRecord('');
                    setIsEdit(false);
                    setShowAddModel(true);
                  }
                }}
                className="px-3 add-new-team"
                style={{
                  color: isWriteAccess() ? 'gray' : '',
                  opacity: isWriteAccess() ? '0.7' : '',
                  cursor: isWriteAccess() ? 'not-allowed' : 'pointer',
                }}
              >
                <span className="icon-22">
                  <svg>
                    <use xlinkHref="#add" />
                  </svg>
                </span>
                Add New
              </Text>
              <Text
                onClick={() => {
                  !isWriteAccess() && setBulkUploadModalVisible(true);
                }}
                className="px-3 add-new-team"
                style={{
                  color: isWriteAccess() ? 'gray' : '',
                  opacity: isWriteAccess() ? '0.7' : '',
                  cursor: isWriteAccess() ? 'not-allowed' : 'pointer',
                }}
              >
                <span className="icon-22">
                  <svg>
                    <use xlinkHref="#add" />
                  </svg>
                </span>
                Bulk Upload Documents
              </Text>
            </div>
          </div>
        </div>
        <div className="custom-line" />
      </div>
      <div className="addTeamDetails">
        <Table
          columns={columns}
          dataSource={rowData}
          className="h-100 pipeline-table equip_table equipPaginationTable"
          scroll={{ y: 'calc(100vh - 300px)' }}
          pagination={
            shipmentDataCount > 0
              ? {
                  position: ['bottomRight'],
                  className: 'fixed-pagination',
                  showSizeChanger: true,
                  total: shipmentDataCount,
                  defaultPageSize: 10,
                  defaultCurrent: 1,
                  pageSize: currentPagesize,
                  current: current,

                  onChange: async (pageNumber, pageSize) => {
                    setCurrent(pageNumber);
                    console.log('setCurrentPagesize', pageSize);
                    setCurrentPagesize(pageSize);
                    var obj = {
                      projectId: projectIdValue,
                      pageSize: pageSize,
                      pageNumber: pageNumber,
                    };
                    await OnGetShipmentsData(obj);
                  },
                }
              : false
          }
          loading={shipmentDataLoading}
        />
        <div className="new-team">
          {/* <Text className='equip_note'>Double Click on table row to display Equipment Logs.</Text> */}
        </div>
      </div>

      {showAddModel && (
        <AddNewShipments
          showAddModel={showAddModel}
          setShowAddModel={setShowAddModel}
          setIsEdit={setIsEdit}
          setEditRecord={setEditRecord}
          editRecord={editRecord}
          isEdit={isEdit}
          OnfetchData={OnfetchData}
          selectedProject={selectedProject}
          adminShipmentTypesList={adminShipmentTypesList}
        ></AddNewShipments>
      )}

      {showFormModel && (
        <ShipmentLogs
          showFormModel={showFormModel}
          setShowFormModel={setShowFormModel}
          selectedData={selectedData}
        />
      )}
      <Modal
        width="45vw"
        className="pipeline-modal"
        title="Delete Shipment"
        centered
        visible={isDeleteModalVisible}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={handleOk}
            loading={deleteLoading}
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          <Text style={{ fontSize: '16px' }}>
            <p>
              Deleting shipments will delete all the corresponding shipment number and related
              information.
            </p>
            Are you sure you want to delete{' '}
            <b style={{ color: '#EF314C' }}>{selectedData && selectedData.shipmentTypeName}</b>{' '}
            shipment?
          </Text>
        </div>
      </Modal>

      <Modal
        width="50vw"
        className="pipeline-modal"
        title="Uploaded Files"
        centered
        visible={isFileModalVisible}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsFileModalVisible(false);
              setFileList([]);
            }}
          >
            Cancel
          </Button>,
        ]}
      >
        <div>
          <Table
            columns={columnsFile}
            dataSource={selectedData.document || []}
            pagination={false}
            width={'100%'}
            className="h-100 pipeline-table equipPaginationTable log-table logDetails_table"
            style={{ marginBottom: '20px' }}
            locale={{
              emptyText: 'No documents uploaded yet',
            }}
          />

          <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: '20px' }}>
            <div style={{ marginBottom: '10px' }}>
              <Text strong>Add New Files:</Text>
            </div>
            <Upload {...fileProps}>
              <Button
                icon={<CloudUploadOutlined />}
                disabled={fileList.length >= 100}
              >
                Select Files (Excel / PDF)
              </Button>
            </Upload>
            {fileList.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <Button
                  type="primary"
                  onClick={handleUploadFiles}
                  loading={uploadLoading}
                  style={{ marginRight: '10px' }}
                >
                  Upload Files
                </Button>
                <Button onClick={() => setFileList([])}>Clear Selection</Button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        width="50vw"
        className="pipeline-modal"
        title="Bulk Upload Shipment Documents"
        centered
        visible={bulkUploadModalVisible}
        footer={[
          <Button
            key="cancel"
            onClick={handleBulkUploadCancel}
          >
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleBulkUploadFiles}
            loading={bulkUploadLoading}
            disabled={bulkFileList.length === 0}
          >
            Upload Files
          </Button>,
        ]}
      >
        <div>
          <div style={{ marginBottom: '20px' }}>
            <Text strong>Select Files to Upload:</Text>
            <br />
            <Text type="secondary">
              Upload multiple files that will be associated with shipment documents.
            </Text>
          </div>

          <Upload {...bulkFileProps}>
            <Button
              icon={<CloudUploadOutlined />}
              disabled={bulkFileList.length >= 100}
            >
              Select Files (Excel / PDF)
            </Button>
          </Upload>

          {bulkFileList.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <Button onClick={() => setBulkFileList([])}>Clear Selection</Button>
              <Text style={{ marginLeft: '10px', color: '#666' }}>
                {bulkFileList.length} file(s) selected
              </Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = ({ ShipmentReducer, ShipmentDashBoardReducer, equip }) => {
  const shipmentsTypes = get(ShipmentReducer, 'shipmentData', []);
  const shipmentTypesLoading = get(ShipmentReducer, 'isShipmentTypeLoading', false);
  const shipmentTypesCount = get(ShipmentReducer, 'isShipmentTypeLoading', false);

  const shipmentData = get(ShipmentDashBoardReducer, 'shipmentListData', []);
  const shipmentDataCount = get(ShipmentDashBoardReducer, 'shipmentsCount', false);
  const shipmentDataLoading = get(ShipmentDashBoardReducer, 'isShipmentsLoading', false);

  const userAccessList = get(equip, 'userAccessList', []);
  const projectTeamMembersAccessList = get(equip, 'projectTeamMembersAccessList', []);

  return {
    shipmentsTypes,
    shipmentTypesLoading,
    shipmentTypesCount,
    shipmentData,
    shipmentDataLoading,
    shipmentDataCount,
    userAccessList,
    projectTeamMembersAccessList,
  };
};

const mapDispatchToProps = {
  onGetShipmentTypes: fetchShipmentTypeDetails,
  OnGetShipmentsData: fetchShipmentDashBoardDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShipmentDashBoardPage);
